/**
 * <menu-sidebar-tabs>
 *
 * A small, self-contained tab widget for the FriendlyDiamonds-style left
 * sidebar mega menu (see snippets/mega-menu-sidebar.liquid). Switches which
 * tab panel (and optional sidebar image) is visible on click, hover, or
 * arrow-key navigation between the tab buttons.
 *
 * Markup contract (all produced by mega-menu-sidebar.liquid):
 * - Each tab button carries [data-menu-sidebar-tab] and aria-controls
 *   pointing at its panel's id.
 * - Each panel carries [data-menu-sidebar-panel] and starts `hidden` unless
 *   it's the first tab.
 * - An optional per-tab image carries [data-menu-sidebar-image] and
 *   [data-for="<tab button id>"], toggled in sync with its tab.
 */
class MenuSidebarTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-menu-sidebar-tab]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab));
      tab.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.activate(tab);
      });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });
  }

  activate(tab) {
    this.tabs.forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', selected ? 'true' : 'false');

      const panel = this.querySelector('#' + CSS.escape(candidate.getAttribute('aria-controls')));
      if (panel) panel.hidden = !selected;

      const image = this.querySelector(
        '[data-menu-sidebar-image][data-for="' + CSS.escape(candidate.id) + '"]'
      );
      if (image) image.hidden = !selected;
    });
  }

  onKeydown(event, tab) {
    const index = this.tabs.indexOf(tab);
    let next = null;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = this.tabs[index + 1] || this.tabs[0];
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = this.tabs[index - 1] || this.tabs[this.tabs.length - 1];
    } else {
      return;
    }

    event.preventDefault();
    next.focus();
    this.activate(next);
  }
}

if (!customElements.get('menu-sidebar-tabs')) {
  customElements.define('menu-sidebar-tabs', MenuSidebarTabs);
}
